(() => {
  const elementName = 'lucia-collection-index';
  if (customElements.get(elementName)) return;

  class LuciaCollectionIndex extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      this.grid = this.querySelector('[data-lci-grid]');
      this.cards = [...this.querySelectorAll('[data-lci-card]')];
      this.searchInput = this.querySelector('[data-lci-search]');
      this.filterPanel = this.querySelector('[data-lci-filter]');
      this.filterToggle = this.querySelector('[data-lci-filter-toggle]');
      this.count = this.querySelector('[data-lci-count]');
      this.empty = this.querySelector('[data-lci-empty]');
      this.categoryFilter = this.querySelector('[data-lci-category-filter]');
      this.categoryToggle = this.querySelector('[data-lci-category-toggle]');
      this.categoryCurrent = this.querySelector('[data-lci-category-current]');
      this.categoryCount = this.querySelector('[data-lci-category-count]');
      this.state = { category: 'All', query: '', sources: new Set() };
      this.canonicalImageSizes = new Map();
      this.layoutPlans = new Map();
      this.layoutPlanSignature = '';
      this.resizeFrame = 0;
      this.observedWidth = 0;
      this.videoObserver = null;

      this.addEventListener('submit', (event) => event.preventDefault(), { signal: this.abortController.signal });
      this.addEventListener('click', (event) => this.handleClick(event), { signal: this.abortController.signal });
      this.addEventListener('input', (event) => this.handleInput(event), { signal: this.abortController.signal });
      this.addEventListener('change', (event) => this.handleChange(event), { signal: this.abortController.signal });
      document.addEventListener('click', (event) => this.handleOutsideClick(event), { signal: this.abortController.signal });
      document.addEventListener('keydown', (event) => this.handleKeydown(event), { signal: this.abortController.signal });

      this.resizeObserver = new ResizeObserver((entries) => {
        const width = Math.round(entries[0]?.contentRect.width || this.clientWidth);
        if (width === this.observedWidth) return;
        this.observedWidth = width;
        this.scheduleLayout();
      });
      this.resizeObserver.observe(this);
      this.cards.forEach((card) => {
        const image = card.querySelector('img');
        if (image && !image.complete) image.addEventListener('load', () => this.scheduleLayout(), { once: true });
      });

      this.setupVideoCovers();
      this.applyFilters();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => {
        this.layoutPlanSignature = '';
        this.scheduleLayout();
      });
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.videoObserver?.disconnect();
      cancelAnimationFrame(this.resizeFrame);
      this.abortController = null;
    }

    setupVideoCovers() {
      const templates = new Map();
      this.querySelectorAll('template[data-lci-video-for]').forEach((template) => {
        if (template.content.querySelector('.lci-card__video')) templates.set(template.dataset.lciVideoFor, template);
      });
      if (!templates.size) return;

      const cards = this.cards.filter((card) => templates.has(card.dataset.lciKey));
      const mount = (card) => {
        if (card.dataset.lciVideoMounted === 'true') return;
        const template = templates.get(card.dataset.lciKey);
        if (!template) return;
        const fragment = template.content.cloneNode(true);
        const media = fragment.querySelector('.lci-card__video');
        const image = card.querySelector('.lci-card__image');
        if (!media || !image) return;

        card.dataset.lciVideoMounted = 'true';
        media.setAttribute('tabindex', '-1');
        media.setAttribute('aria-hidden', 'true');
        if (media.tagName === 'VIDEO') {
          media.muted = true;
          media.addEventListener('loadeddata', () => media.classList.add('is-ready'), { once: true });
          media.addEventListener('error', () => media.remove(), { once: true });
        } else {
          media.addEventListener('load', () => media.classList.add('is-ready'), { once: true });
        }

        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (reduceMotion && media.tagName === 'VIDEO') {
          media.removeAttribute('autoplay');
        } else if (reduceMotion && media.tagName === 'IFRAME') {
          const source = new URL(media.getAttribute('src'), window.location.href);
          source.searchParams.set('autoplay', '0');
          media.setAttribute('src', source.toString());
        }

        image.insertBefore(fragment, image.querySelector('.lci-card__arrow'));
        if (media.tagName === 'VIDEO' && !reduceMotion) media.play().catch(() => {});
      };

      if (!('IntersectionObserver' in window)) {
        cards.forEach(mount);
        return;
      }
      this.videoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          mount(entry.target);
          this.videoObserver.unobserve(entry.target);
        });
      }, { rootMargin: '320px 0px' });
      cards.forEach((card) => this.videoObserver.observe(card));
    }

    handleClick(event) {
      const category = event.target.closest('[data-lci-category]');
      if (category) {
        this.state.category = category.dataset.lciCategory;
        this.querySelectorAll('[data-lci-category]').forEach((button) => {
          const active = button === category;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
        this.updateCategoryFilterCurrent();
        this.setCategoryFilterOpen(false);
        this.applyFilters();
        return;
      }

      if (event.target.closest('[data-lci-category-toggle]')) {
        const expanded = this.categoryToggle.getAttribute('aria-expanded') === 'true';
        this.closeTags();
        this.setCategoryFilterOpen(!expanded);
        return;
      }

      const toggle = event.target.closest('.lci-card__toggle');
      if (toggle) {
        event.stopPropagation();
        const card = toggle.closest('.lci-card');
        const willExpand = card.dataset.expanded !== 'true';
        this.closeTags(card);
        this.setExpanded(card, willExpand);
        return;
      }

      if (event.target.closest('[data-lci-filter-toggle]')) {
        const expanded = this.filterToggle.getAttribute('aria-expanded') === 'true';
        this.filterToggle.setAttribute('aria-expanded', String(!expanded));
        this.filterPanel.hidden = expanded;
        return;
      }

      if (event.target.closest('[data-lci-clear]')) this.clearFilters();
    }

    handleInput(event) {
      if (!event.target.matches('[data-lci-search]')) return;
      this.state.query = event.target.value.trim().toLowerCase();
      this.applyFilters();
    }

    handleChange(event) {
      if (!event.target.matches('[data-lci-source]')) return;
      if (event.target.checked) this.state.sources.add(event.target.value);
      else this.state.sources.delete(event.target.value);
      this.applyFilters();
    }

    handleOutsideClick(event) {
      if (this.categoryFilter.dataset.open === 'true' && !event.target.closest('[data-lci-category-filter]')) {
        event.preventDefault();
        this.setCategoryFilterOpen(false);
        return;
      }
      if (!this.contains(event.target) || !event.target.closest('.lci-card[data-expanded="true"]')) this.closeTags();
    }

    handleKeydown(event) {
      if (event.key !== 'Escape') return;
      const categoryFilterWasOpen = this.categoryToggle.getAttribute('aria-expanded') === 'true';
      this.setCategoryFilterOpen(false, categoryFilterWasOpen);
      this.closeTags(null, true);
    }

    updateCategoryFilterCurrent() {
      const count = this.state.category === 'All'
        ? this.cards.length
        : this.cards.filter((card) => new Set((card.dataset.source || '').split('|')).has(this.state.category)).length;
      this.categoryCurrent.textContent = this.state.category;
      this.categoryCount.textContent = String(count);
      this.categoryToggle.setAttribute('aria-label', 'Browse by category: ' + this.state.category + ', ' + count + ' collections');
    }

    setCategoryFilterOpen(open, returnFocus = false) {
      this.categoryFilter.dataset.open = String(open);
      this.categoryToggle.setAttribute('aria-expanded', String(open));
      if (returnFocus) this.categoryToggle.focus();
    }

    clearFilters() {
      this.state = { category: 'All', query: '', sources: new Set() };
      this.setCategoryFilterOpen(false);
      this.searchInput.value = '';
      this.querySelectorAll('[data-lci-source]').forEach((input) => { input.checked = false; });
      this.querySelectorAll('[data-lci-category]').forEach((button) => {
        const active = button.dataset.lciCategory === 'All';
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', String(active));
      });
      this.updateCategoryFilterCurrent();
      this.applyFilters();
    }

    applyFilters() {
      this.closeTags();
      let visible = 0;
      this.cards.forEach((card) => {
        const cardSources = new Set((card.dataset.source || '').split('|'));
        const matchesCategory = this.state.category === 'All' || cardSources.has(this.state.category);
        const matchesQuery = !this.state.query || card.dataset.search.includes(this.state.query);
        const matchesSource = this.state.sources.size === 0 || [...this.state.sources].some((source) => cardSources.has(source));
        card.hidden = !(matchesCategory && matchesQuery && matchesSource);
        if (!card.hidden) visible += 1;
      });
      this.count.textContent = String(visible);
      this.empty.hidden = visible !== 0;
      this.grid.hidden = visible === 0;
      this.resizeMasonry();
    }

    scheduleLayout() {
      cancelAnimationFrame(this.resizeFrame);
      this.resizeFrame = requestAnimationFrame(() => {
        this.resizeFrame = 0;
        this.resizeMasonry();
      });
    }

    clearLayout() {
      this.grid.classList.remove('is-positioned');
      this.grid.style.removeProperty('--lci-masonry-height');
      this.cards.forEach((card) => {
        card.classList.remove('is-tail-card');
        card.style.removeProperty('--lci-tail-image-height');
        card.style.removeProperty('--lci-masonry-x');
        card.style.removeProperty('--lci-masonry-y');
        card.style.removeProperty('--lci-masonry-width');
        card.style.removeProperty('grid-row-end');
      });
      this.grid.dataset.tailBalanced = 'false';
    }

    resizeMasonryItem(card) {
      const styles = getComputedStyle(this.grid);
      const rowHeight = Number.parseFloat(styles.getPropertyValue('grid-auto-rows'));
      const rowGap = Number.parseFloat(styles.getPropertyValue('row-gap')) || 0;
      const cardHeight = this.measureCardHeight(card);
      card.style.gridRowEnd = 'span ' + Math.ceil((cardHeight + rowGap) / (rowHeight + rowGap));
    }

    measureCardHeight(card) {
      const image = card.querySelector('.lci-card__image');
      const caption = card.querySelector('.lci-card__caption');
      return image.getBoundingClientRect().height + caption.getBoundingClientRect().height;
    }

    resizeMasonry() {
      this.clearLayout();
      const visibleCards = this.cards.filter((card) => !card.hidden);
      if (!visibleCards.length) return;
      const geometry = this.getColumnGeometry();
      if (geometry.columnCount < 2) return;
      const signature = [Math.round(geometry.gridWidth), geometry.columnCount, Math.round(geometry.cardWidth * 100)].join('|');
      if (signature !== this.layoutPlanSignature) {
        this.prepareBalancedLayouts(geometry);
        this.layoutPlanSignature = signature;
      }

      this.applyCanonicalImageSizes(this.cards);
      visibleCards.forEach((card) => this.resizeMasonryItem(card));
      const isStandardView = !this.state.query && this.state.sources.size === 0 && this.layoutPlans.has(this.state.category);
      if (isStandardView) this.positionLayoutPlan(this.layoutPlans.get(this.state.category), geometry);
    }

    getColumnGeometry() {
      const styles = getComputedStyle(this.grid);
      const columns = styles.gridTemplateColumns.trim().split(/\s+/).filter(Boolean);
      const columnCount = Math.max(1, columns.length);
      const columnGap = Number.parseFloat(styles.columnGap) || 0;
      const gridWidth = this.grid.getBoundingClientRect().width;
      const columnWidths = columns.map((column) => Number.parseFloat(column));
      const measuredColumns = columnWidths.every((width) => Number.isFinite(width));
      const cardWidth = measuredColumns
        ? columnWidths[0]
        : (gridWidth - columnGap * (columnCount - 1)) / columnCount;
      let offset = 0;
      const lefts = Array.from({ length: columnCount }, (_, index) => {
        const left = offset;
        offset += (measuredColumns ? columnWidths[index] : cardWidth) + columnGap;
        return left;
      });
      return {
        columnCount,
        columnGap,
        gridWidth,
        cardWidth,
        lefts,
      };
    }

    prepareBalancedLayouts(geometry) {
      const hiddenStates = this.cards.map((card) => card.hidden);
      this.cards.forEach((card) => { card.hidden = false; });
      this.cards.forEach((card) => {
        card.style.setProperty('--lci-masonry-x', '0px');
        card.style.setProperty('--lci-masonry-y', '0px');
        card.style.setProperty('--lci-masonry-width', geometry.cardWidth.toFixed(2) + 'px');
      });
      this.grid.classList.add('is-positioned');
      const verticalGap = Number.parseFloat(getComputedStyle(this.cards[0]).marginBottom) || 28;
      const records = this.cards.map((card, index) => {
        const image = card.querySelector('.lci-card__image');
        const imageRect = image.getBoundingClientRect();
        const captionRect = card.querySelector('.lci-card__caption').getBoundingClientRect();
        const groups = (card.dataset.source || '').split('|').filter(Boolean);
        return {
          card,
          index,
          groups,
          exclusiveGroup: groups.length === 1 ? groups[0] : '',
          width: imageRect.width,
          baseImageHeight: imageRect.height,
          imageHeight: imageRect.height,
          chromeHeight: captionRect.height,
        };
      });
      this.grid.classList.remove('is-positioned');
      this.cards.forEach((card) => {
        card.style.removeProperty('--lci-masonry-x');
        card.style.removeProperty('--lci-masonry-y');
        card.style.removeProperty('--lci-masonry-width');
      });

      this.canonicalImageSizes.clear();
      this.layoutPlans.clear();
      const categories = ['Lighting', 'Featured Series', 'Rooms', 'By Style', 'By Material'];
      categories.forEach((category, categoryIndex) => {
        const categoryRecords = records.filter((record) => record.groups.includes(category));
        const plan = this.findCategoryPlan(categoryRecords, geometry.columnCount, verticalGap, category, categoryIndex);
        if (!plan) return;
        this.layoutPlans.set(category, this.orderPlanColumns(plan));
      });

      this.layoutPlans.set('All', this.orderPlanColumns(this.findAllPlan(records, geometry.columnCount, verticalGap)));
      this.solveImageHeights(records, verticalGap);
      records.forEach((record) => {
        this.canonicalImageSizes.set(record.card, {
          width: record.width,
          height: record.imageHeight,
          baseHeight: record.baseImageHeight,
        });
      });
      hiddenStates.forEach((hidden, index) => { this.cards[index].hidden = hidden; });
    }

    recordHeight(record) {
      return record.imageHeight + record.chromeHeight;
    }

    binHeight(bin, verticalGap) {
      return bin.reduce((sum, record) => sum + this.recordHeight(record), 0) + verticalGap * Math.max(0, bin.length - 1);
    }

    createRandom(seed) {
      let value = seed >>> 0;
      return () => {
        value ^= value << 13;
        value ^= value >>> 17;
        value ^= value << 5;
        return (value >>> 0) / 4294967296;
      };
    }

    shuffle(items, random) {
      const result = [...items];
      for (let index = result.length - 1; index > 0; index -= 1) {
        const swapIndex = Math.floor(random() * (index + 1));
        [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
      }
      return result;
    }

    balancedCapacities(itemCount, columnCount) {
      const baseCount = Math.floor(itemCount / columnCount);
      const extraCount = itemCount % columnCount;
      return Array.from({ length: columnCount }, (_, index) => baseCount + (index < extraCount ? 1 : 0));
    }

    findCategoryPlan(records, columnCount, verticalGap, category, categoryIndex) {
      if (records.length < columnCount) return null;
      const exclusive = records.filter((record) => record.exclusiveGroup === category);
      if (exclusive.length < columnCount) return null;
      const capacities = this.balancedCapacities(records.length, columnCount);
      const random = this.createRandom(records.length * 2654435761 + categoryIndex * 7919 + Math.round(records[0].width * 100));
      let best = null;

      for (let trial = 0; trial < 1400; trial += 1) {
        const trialCapacities = this.shuffle(capacities, random);
        const selectedExclusive = this.shuffle(exclusive, random).slice(0, columnCount);
        const selectedCards = new Set(selectedExclusive.map((record) => record.card));
        const bins = trialCapacities.map((capacity, index) => ({
          capacity,
          records: [selectedExclusive[index]],
          total: this.recordHeight(selectedExclusive[index]),
        }));
        const remaining = records
          .filter((record) => !selectedCards.has(record.card))
          .map((record) => ({ record, noise: random() * records[0].width * .35 }))
          .sort((a, b) => (this.recordHeight(b.record) + b.noise) - (this.recordHeight(a.record) + a.noise));

        remaining.forEach(({ record }) => {
          const available = bins.filter((bin) => bin.records.length < bin.capacity);
          const minimum = Math.min(...available.map((bin) => bin.total));
          const near = available.filter((bin) => bin.total <= minimum + records[0].width * .18);
          const bin = near[Math.floor(random() * near.length)] || available[0];
          bin.records.push(record);
          bin.total += this.recordHeight(record) + verticalGap;
        });

        const target = Math.max(...bins.map((bin) => bin.total));
        let maxRelativeChange = 0;
        let totalRelativeChange = 0;
        let totalChange = 0;
        bins.forEach((bin) => {
          const deficit = Math.max(0, target - bin.total);
          const adjustable = bin.records.filter((record) => record.exclusiveGroup === category);
          const share = adjustable.length ? deficit / adjustable.length : Number.POSITIVE_INFINITY;
          adjustable.forEach((record) => {
            const relative = share / Math.max(1, record.baseImageHeight);
            maxRelativeChange = Math.max(maxRelativeChange, relative);
            totalRelativeChange += relative;
          });
          totalChange += deficit;
        });
        const score = maxRelativeChange * 100000 + totalRelativeChange * 1000 + totalChange;
        if (!best || score < best.score) best = { score, plan: bins.map((bin) => bin.records) };
      }
      return best?.plan || null;
    }

    findAllPlan(records, columnCount, verticalGap) {
      const capacities = this.balancedCapacities(records.length, columnCount);
      const random = this.createRandom(records.length * 2246822519 + Math.round(records[0].width * 100));
      let best = null;

      for (let trial = 0; trial < 1800; trial += 1) {
        const trialCapacities = this.shuffle(capacities, random);
        const bins = trialCapacities.map((capacity) => ({ capacity, records: [], total: 0 }));
        const ordered = records
          .map((record) => ({ record, noise: random() * records[0].width * .28 }))
          .sort((a, b) => (this.recordHeight(b.record) + b.noise) - (this.recordHeight(a.record) + a.noise));
        ordered.forEach(({ record }) => {
          const available = bins.filter((bin) => bin.records.length < bin.capacity);
          const minimum = Math.min(...available.map((bin) => bin.total));
          const near = available.filter((bin) => bin.total <= minimum + records[0].width * .12);
          const bin = near[Math.floor(random() * near.length)] || available[0];
          bin.records.push(record);
          bin.total += this.recordHeight(record) + (bin.records.length > 1 ? verticalGap : 0);
        });
        const target = Math.max(...bins.map((bin) => bin.total));
        const adjustments = bins.map((bin) => (target - bin.total) / Math.max(1, bin.records.length - 1));
        const score = Math.max(...adjustments) * 1000 + adjustments.reduce((sum, adjustment) => sum + adjustment, 0);
        if (!best || score < best.score) best = { score, plan: bins.map((bin) => bin.records) };
      }
      return best.plan;
    }

    solveImageHeights(records, verticalGap) {
      const relativeChanges = new Map();
      const equations = [];
      const adjustable = new Set(records.filter((record) => record.exclusiveGroup));

      this.layoutPlans.forEach((plan) => {
        const reference = plan[0];
        const referenceHeight = this.binHeight(reference, verticalGap);
        for (let columnIndex = 1; columnIndex < plan.length; columnIndex += 1) {
          const column = plan[columnIndex];
          const terms = [];
          reference.forEach((record) => {
            if (adjustable.has(record)) terms.push({ record, coefficient: -record.baseImageHeight });
          });
          column.forEach((record) => {
            if (adjustable.has(record)) terms.push({ record, coefficient: record.baseImageHeight });
          });
          const norm = terms.reduce((sum, term) => sum + term.coefficient * term.coefficient, 0);
          if (!norm) continue;
          equations.push({
            difference: this.binHeight(column, verticalGap) - referenceHeight,
            norm,
            terms,
          });
        }
      });

      for (let pass = 0; pass < 12000; pass += 1) {
        let maxResidual = 0;
        equations.forEach((equation) => {
          const residual = equation.difference + equation.terms.reduce(
            (sum, term) => sum + term.coefficient * (relativeChanges.get(term.record) || 0),
            0,
          );
          maxResidual = Math.max(maxResidual, Math.abs(residual));
          const correction = residual / equation.norm;
          equation.terms.forEach((term) => {
            const minimum = term.record.width / term.record.baseImageHeight - 1;
            const maximum = term.record.width * 1.78 / term.record.baseImageHeight - 1;
            const next = (relativeChanges.get(term.record) || 0) - correction * term.coefficient;
            relativeChanges.set(
              term.record,
              Math.min(maximum, Math.max(minimum, next)),
            );
          });
        });
        if (maxResidual < .001) break;
      }

      records.forEach((record) => {
        const relativeChange = relativeChanges.get(record) || 0;
        record.imageHeight = record.baseImageHeight * (1 + relativeChange);
      });
    }

    orderPlanColumns(plan) {
      return [...plan]
        .map((bin) => [...bin].sort((a, b) => a.index - b.index))
        .sort((a, b) => a[0].index - b[0].index);
    }

    applyCanonicalImageSizes(cards) {
      cards.forEach((card) => {
        const size = this.canonicalImageSizes.get(card);
        if (!size) return;
        card.classList.add('is-tail-card');
        card.style.setProperty('--lci-tail-image-height', size.height.toFixed(2) + 'px');
      });
    }

    positionLayoutPlan(plan, geometry) {
      const verticalGap = Number.parseFloat(getComputedStyle(this.cards[0]).marginBottom) || 28;
      plan.forEach((bin, columnIndex) => {
        bin.forEach((record) => {
          record.card.style.setProperty('--lci-masonry-x', geometry.lefts[columnIndex].toFixed(2) + 'px');
          record.card.style.setProperty('--lci-masonry-y', '0px');
          record.card.style.setProperty('--lci-masonry-width', geometry.cardWidth.toFixed(2) + 'px');
        });
      });
      this.grid.classList.add('is-positioned');
      const measured = plan.map((bin) => ({
        records: bin,
        naturalHeight: bin.reduce((sum, record) => sum + this.measureCardHeight(record.card), 0) + verticalGap * Math.max(0, bin.length - 1),
      }));
      const targetHeight = Math.max(...measured.map((bin) => bin.naturalHeight));

      measured.forEach((bin, columnIndex) => {
        let y = 0;
        bin.records.forEach((record, itemIndex) => {
          record.card.style.setProperty('--lci-masonry-y', y.toFixed(2) + 'px');
          y += this.measureCardHeight(record.card);
          if (itemIndex < bin.records.length - 1) y += verticalGap;
        });
      });

      this.grid.style.setProperty('--lci-masonry-height', targetHeight.toFixed(2) + 'px');
      this.grid.dataset.tailBalanced = 'true';
      this.grid.dataset.balanceError = (
        Math.max(...measured.map((bin) => bin.naturalHeight))
        - Math.min(...measured.map((bin) => bin.naturalHeight))
      ).toFixed(2);
    }

    setExpanded(card, expanded, returnFocus = false) {
      const toggle = card.querySelector('.lci-card__toggle');
      const title = card.querySelector('.lci-card__title').textContent;
      card.dataset.expanded = String(expanded);
      toggle.setAttribute('aria-expanded', String(expanded));
      toggle.setAttribute('aria-label', (expanded ? 'Hide' : 'Show') + ' tags for ' + title);
      if (expanded) this.positionTags(card);
      else card.classList.remove('tags-align-right', 'tags-open-up');
      if (returnFocus) toggle.focus();
    }

    closeTags(exceptCard = null, returnFocus = false) {
      this.querySelectorAll('.lci-card[data-expanded="true"]').forEach((card) => {
        if (card !== exceptCard) this.setExpanded(card, false, returnFocus);
      });
    }

    positionTags(card) {
      const panel = card.querySelector('.lci-card__tags');
      card.classList.remove('tags-align-right', 'tags-open-up');
      let rect = panel.getBoundingClientRect();
      const gutter = 12;
      if (rect.right > window.innerWidth - gutter) {
        card.classList.add('tags-align-right');
        rect = panel.getBoundingClientRect();
      }
      if (rect.bottom > window.innerHeight - gutter && rect.top - rect.height > gutter) card.classList.add('tags-open-up');
    }
  }

  customElements.define(elementName, LuciaCollectionIndex);
})();
